import { create } from "zustand";
import { persist } from "zustand/middleware";
import { queryClient } from "@/lib/queryClient";
import { useOnboardingStore } from "./onboardingStore";

interface AuthState {
  token: string | null;
  email: string | null;
  setAuth: (token: string, email: string) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      email: null,
      setAuth: (token, email) => set({ token, email }),
      logout: () => set({ token: null, email: null }),
    }),
    { name: "mavinty-auth" },
  ),
);

// Covers explicit login/logout and persist.rehydrate() after a change in another
// tab. Clearing destroys pending queries too, so their results cannot repopulate
// a later session. App also remounts the route tree to discard local UI state.
useAuthStore.subscribe((state, previous) => {
  if (state.token === previous.token && state.email === previous.email) return;
  queryClient.clear();
  useOnboardingStore.getState().reset();
  useOnboardingStore.getState().bindToAccount(state.token ? state.email : null);
  if (!state.token) useOnboardingStore.persist.clearStorage();
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.removeItem("mavinty_paywall_reason");
  }
});

// An old unowned draft, or a draft from an account switched in another tab,
// must not be hydrated into this session. Same-account refresh can still resume.
const initialAuth = useAuthStore.getState();
useOnboardingStore.getState().bindToAccount(initialAuth.token ? initialAuth.email : null);
if (!initialAuth.token) {
  useOnboardingStore.getState().reset();
  useOnboardingStore.persist.clearStorage();
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === "mavinty-auth" || event.key === null) {
      if (localStorage.getItem("mavinty-auth") === null) {
        useAuthStore.getState().logout();
      } else {
        void useAuthStore.persist.rehydrate();
      }
    }
  });
}
